import {
    Box, Button,
    Chip, Drawer, FormControl, FormControlLabel, FormLabel, LinearProgress,
    List,
    ListItem, ListItemButton,
    MenuItem, Radio, RadioGroup,
    Select,
    SelectChangeEvent, ToggleButton,
    Typography, useMediaQuery, useTheme, ToggleButtonGroup, Pagination
} from "@mui/material";
import Grid from '@mui/material/Grid2';

import {
    BasicDescriptorInfo,
    DateTimeRange,
    FilterItem,
    SORT_MODES,
    SimilarObjectsDict,
    EXTRA_FILTERS
} from "consts/khw-consts";
import {Suspense, use, useEffect, useState, lazy, ChangeEvent} from "react";
import {translateKey} from "utils/fieldsFormatters";
import {LanguageContext} from "context/LanguageContext";
import {
    fetchDescriptorsList,
    fetchDescriptorsListByAlternateNames,
    searchBySimilarPhrases
} from "services/khwApi";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useQueries} from "@tanstack/react-query";
import _, {pickBy, omit} from 'lodash'
import CloseIcon from "@mui/icons-material/Close";
import DescriptorSimilarObjects from "./DescriptorSimilarObjects";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GridViewIcon from '@mui/icons-material/GridView';
import {useCookies} from "react-cookie";
import {highlightName} from "utils/highlightName";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SEO from "../common/SEO";
import {renderInitialFiltersWithSource} from "../../utils/renderInitialFiltersWithSource";


const DescriptorCard = lazy(() => import("components/descriptorsList/DescriptorCard"));
const FiltersBox = lazy(() => import("components/descriptorsList/FiltersBox"));
const BreadcrumbsRow = lazy(() => import("components/common/BreadcrumbsRow"));
const EmptyDivider = lazy(() => import("components/common/EmptyDivider"));
const MARC21RecordDialog = lazy(() => import("components/descriptorInfo/dialogs/MARC21RecordDialog"));


const DescriptorsList = () => {
    const {language} = use(LanguageContext);

    const INITIAL_PAGE_SIZE = 30
    const [queryParams, setQueryParams] = useSearchParams()
    const [result, setResult] = useState<BasicDescriptorInfo[]>([])
    const [alternateNamesResult, setAlternateNamesResult] = useState<BasicDescriptorInfo[]>([])
    const [filters, setFilters] = useState<FilterItem[]>([...renderInitialFiltersWithSource(queryParams.getAll('sourceName'))])
    const [selectedSort, setSelectedSort] = useState<string>(SORT_MODES.BY_CREATION_DATE_DESCENDING)
    const [showSortDrawer, setShowSortDrawer] = useState(false)
    const [showFilterDrawer, setShowFilterDrawer] = useState(false)
    const [totalPages, setTotalPages] = useState(0)
    const [displayPage, setDisplayPage] = useState(1)
    const [totalRecords, setTotalRecords] = useState(0)
    const [alternateNamesTotalRecords, setAlternateNamesTotalRecords] = useState(0)
    const [aggregations, setAggregations] = useState([])
    const theme = useTheme();
    const mobileView = useMediaQuery(theme.breakpoints.between('xs', 'md'));
    const searchedText = queryParams.get('searchedText')
    const [selectedFilters, setSelectedFilters] = useState<any>(searchedText && searchedText !== '' ? {searchedText: searchedText} :{})
    const navigate = useNavigate()
    const [cookies, setCookie] = useCookies(['displayMode']);
    const [loading, setLoading] = useState(false)
    const [shouldFetchResults, setShouldFetchResults] = useState(false);
    const [shouldFetchAlternativeNames, setShouldFetchAlternativeNames] = useState(false);
    const [resultsQuery, alternateNamesQuery] = useQueries({
        queries: [
            {
                queryKey: ['descriptors', selectedFilters],
                queryFn: () => fetchDescriptorsList(selectedFilters),
                enabled: shouldFetchResults
            },
            {
                queryKey: ['alternateNamesDescriptors', selectedFilters],
                queryFn: () => fetchDescriptorsListByAlternateNames(selectedFilters),
                enabled: shouldFetchAlternativeNames
            },
        ]

    })

    const [similarRecords, setSimilarRecords] = useState<SimilarObjectsDict | null>(null)
    const [displayMode, setDisplayMode] = useState(cookies.displayMode ?? 'cards')
    const [openSeeMARC21RecordDialog, setOpenSeeMARC21RecordDialog] = useState(false)
    const [selectedNlpId, setSelectedNlpId] = useState('')
    const [selectedSourceName, setSelectedSourceName] = useState('')
    const [selectedExtraFilters, setSelectedExtraFilters] = useState<{ [key: string]: (string)[] }>({})

    useEffect(() => {
        return () => {
            setFilters([...renderInitialFiltersWithSource(queryParams.getAll('sourceName'))])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onCloseSeeMARC21RecordDialog = () => {
        setOpenSeeMARC21RecordDialog(false)
        setSelectedSourceName('')
        setSelectedNlpId('')
    }
    useEffect(() => {
        //fetching classic search results
        if (resultsQuery?.data) {
            const {result, totalPages, totalHits, aggregations} = resultsQuery?.data.data
            setResult(result)
            setTotalPages(totalPages)
            setTotalRecords(totalHits)
            setAggregations(aggregations)
            // setLoading(false)

        }
        //fetching alternate names search results
        const searchedText = queryParams.get('searchedText')
        if (alternateNamesQuery?.data && searchedText) {
            const {result, totalHits} = alternateNamesQuery?.data.data
            setAlternateNamesResult(result)
            setAlternateNamesTotalRecords(totalHits)
        }
    }, [resultsQuery, alternateNamesQuery, queryParams])

    useEffect(() => {
        if (result.length > 0)
            setLoading(false)
    }, [result]);

    useEffect(() => {
        setLoading(false)
    }, [similarRecords]);

    useEffect(() => {
        setLoading(true)
        setShouldFetchResults(true)
        if (searchedText && searchedText !== '') {
            setShouldFetchAlternativeNames(true)
        } else {
            setShouldFetchAlternativeNames(false)
        }
    }, [selectedFilters,searchedText]);

    const navigateToDescriptorDetails = (nlpId: string) => {
        if (searchedText) localStorage.setItem('searchedText', searchedText)
        navigate(`/descriptor-details/${nlpId}`)
    }

    useEffect(() => {
        if (searchedText && result.length === 0) {
            searchBySimilarPhrases(searchedText).then(res => {
                setSimilarRecords(res.data.similarPhrasesBySource)
            }).catch(err => {
                setLoading(false)
                console.log(err)
            })
        } else if (!searchedText) {
            setAlternateNamesResult([])
            setAlternateNamesTotalRecords(0)
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result])

    const getDDMMYYYDateFormat = (date: Date) => `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`

    const updateFiltersAndSelectedFilters = () => {
        const descriptorType = queryParams.getAll('descriptorType');
        const sourceName = queryParams.getAll('sourceName') ?? 'DBN';
        const searchedText = queryParams.get('searchedText');
        const page = !!queryParams.get('page') ? Number(queryParams.get('page')) : 0;
        const size = !!queryParams.get('size') ? Number(queryParams.get('size')) : INITIAL_PAGE_SIZE;
        const order = !!queryParams.get('order') ? queryParams.get('order') : 'DESC';
        let sortBy = queryParams.get('sortBy');

        if (searchedText && searchedText !== "" && sortBy === null) {
            sortBy = 'SCORE';
            setSelectedSort(SORT_MODES.BY_SCORE_DESCENDING);
        } else if (sortBy === null) {
            sortBy = 'CREATED_TIME';
            setSelectedSort(SORT_MODES.BY_CREATION_DATE_DESCENDING);
        } else {
            const currentSelectedSort = sortBy + '__' + order;
            setSelectedSort(currentSelectedSort);
        }

        const createdTimeFrom = queryParams.get('createdTimeFrom');
        const createdTimeTo = queryParams.get('createdTimeTo');
        const updatedTimeFrom = queryParams.get('updatedTimeFrom');
        const updatedTimeTo = queryParams.get('updatedTimeTo');

        const extraFiltersMap: { [key: string]: (string)[] } = {}
        EXTRA_FILTERS.forEach(extraFilter => {
            const values = queryParams.getAll(extraFilter);
            if (values.length > 0) {
                extraFiltersMap[extraFilter] = values;
            }
        });
        if (JSON.stringify(extraFiltersMap) !== JSON.stringify(selectedExtraFilters)) {
            setSelectedExtraFilters(extraFiltersMap);
        }

        let queryFilters = filters.map(item => {
            item.isSelected = (item.type === 'descriptor_type' && descriptorType.includes(item.fullName)) ||
                (item.type === 'source' && sourceName.includes(item.fullName));
            if (item.fullName === 'creation_date' && (!!createdTimeFrom || !!createdTimeTo)) {
                const dateFrom = createdTimeFrom ? new Date(Date.parse(createdTimeFrom)) : null;
                const dateTo = createdTimeTo ? new Date(Date.parse(createdTimeTo)) : null;
                item = {...item, value: {from: dateFrom, to: dateTo}, isSelected: true};
            } else if (item.fullName === 'modification_date' && (!!updatedTimeFrom || !!updatedTimeTo)) {
                const dateFrom = updatedTimeFrom ? new Date(Date.parse(updatedTimeFrom)) : null;
                const dateTo = updatedTimeTo ? new Date(Date.parse(updatedTimeTo)) : null;
                item = {...item, value: {from: dateFrom, to: dateTo}, isSelected: true};
            }
            return item;
        });

        if (JSON.stringify(queryFilters) !== JSON.stringify(filters)) {
            setFilters(queryFilters);
        }


        const newSelectedFilters = (pickBy({
            ...selectedFilters,
            ...extraFiltersMap,
            descriptorType,
            sourceName,
            page,
            size,
            order,
            sortBy,
            searchedText,
            createdTimeFrom,
            createdTimeTo,
            updatedTimeFrom,
            updatedTimeTo
        }, v => !!v));
        if (JSON.stringify(newSelectedFilters) !== JSON.stringify(selectedFilters)) {
            setSelectedFilters(newSelectedFilters);
        }
        setDisplayPage(page + 1)

    }


    useEffect(() => {
        updateFiltersAndSelectedFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams]);


    const updateFiltersAndQueryParams = (filterType: string, newFilters: FilterItem[]) => {
        let page = '0'

        if (filterType === 'descriptor_type') {
            const selectedDescriptors = newFilters.filter(item => item.isSelected && item.type === 'descriptor_type').map(item => `${item.fullName}`)

            const updatedFilters =  pickBy({
                ...selectedFilters,
                page,
                descriptorType: selectedDescriptors
            }, v => !!v)

            // @ts-ignore
            setQueryParams(updatedFilters);
            setSelectedFilters(updatedFilters);

        } else if (filterType === 'source') {
            const selectedSources = newFilters.filter(item => item.isSelected && item.type === 'source').map(item => item.fullName)

            const updatedFilters = pickBy({
                ...selectedFilters,
                page,
                sourceName: selectedSources
            }, v => !!v)

            // @ts-ignore
            setQueryParams(updatedFilters);
            setSelectedFilters(updatedFilters);

        } else if (filterType === 'date') {
            const createdTime = newFilters.find(item => item.fullName === 'creation_date')?.value
            const updatedTime = newFilters.find(item => item.fullName === 'modification_date')?.value
            let createdTimeFrom, createdTimeTo, updatedTimeFrom, updatedTimeTo
            if (createdTime) {
                const {from, to} = createdTime
                createdTimeFrom = !!from ? getDDMMYYYDateFormat(from) : undefined
                createdTimeTo = !!to ? getDDMMYYYDateFormat(to) : undefined
            }

            if (updatedTime) {
                const {from, to} = updatedTime
                updatedTimeFrom = !!from ? getDDMMYYYDateFormat(from) : undefined
                updatedTimeTo = !!to ? getDDMMYYYDateFormat(to) : undefined
            }

            // @ts-ignore
            setQueryParams(pickBy({
                ...selectedFilters,
                page,
                createdTimeFrom: createdTimeFrom,
                createdTimeTo: createdTimeTo,
                updatedTimeFrom: updatedTimeFrom,
                updatedTimeTo: updatedTimeTo
            }, v => !!v))

            setSelectedFilters(pickBy({
                ...selectedFilters,
                page,
                createdTimeFrom: createdTimeFrom,
                createdTimeTo: createdTimeTo,
                updatedTimeFrom: updatedTimeFrom,
                updatedTimeTo: updatedTimeTo
            }, v => !!v))

        }
    }

    const handleSortModeChange = (event: SelectChangeEvent) => {
        const sortOrder = event.target.value.split('__')
        const sortBy = sortOrder[0]
        const order = sortOrder[1]
        setSelectedSort(event.target.value as string);
        const cleanedFilters = pickBy(selectedFilters, v => !!v)
        setSelectedFilters({...cleanedFilters, order, sortBy})
        setQueryParams({...cleanedFilters, order, sortBy})
    };

    const onSelectExtraFilter = (filterName: string, value: string, isDate?: boolean) => {
        const extraFilters = selectedExtraFilters
        if (filterName in extraFilters && !isDate) {
            if (extraFilters[filterName].includes(value)) { // removing filter if already exists
                extraFilters[filterName] = extraFilters[filterName].filter(f => f !== value)
            } else {

                extraFilters[filterName].push(value)
            }
        } else if (!(filterName in extraFilters) || isDate) {
            extraFilters[filterName] = [value]
        }

        setSelectedExtraFilters(extraFilters)
        setQueryParams({...selectedFilters, ...extraFilters})
        setSelectedFilters({...selectedFilters, ...extraFilters})
    }

    const onSelectMultipleExtraFilters = (filterName: string, values: string[]) => {
        const extraFilters = selectedExtraFilters
        extraFilters[filterName] = values
        setSelectedExtraFilters(extraFilters)
        setQueryParams({...selectedFilters, ...extraFilters})
        setSelectedFilters({...selectedFilters, ...extraFilters})

    }
    const onSelectFilter = (selectedFilterName: string, type: string) => {
        let cleanExtra = false
        const newFilters = filters.map(item => {
            if (item.fullName === selectedFilterName) {
                if (item.isSelected && type === 'descriptor_type' && !_.isEmpty(selectedExtraFilters)) {
                    cleanExtra = true
                }
                item.isSelected = !item.isSelected
            }
            return item
        })

        if (cleanExtra) {
            cleanExtraFilters()
            // setFilters(newFilters)
        } else {
            setFilters(newFilters)
            updateFiltersAndQueryParams(type, newFilters)
        }

    }

    const onSelectDateFilter = (name: string, value: DateTimeRange) => {
        const newFilters = filters.map(item => {
            if (item.fullName === name) {
                item.value = value
                item.isSelected = !(value.from === null && value.to === null)
            }
            return item
        })
        setFilters(newFilters)
        updateFiltersAndQueryParams('date', newFilters)

    }

    const onChangePage = (event: ChangeEvent<unknown>, value: number) => {
        const cleanedFilters = pickBy(selectedFilters, v => !!v)
        setQueryParams({...cleanedFilters, page: (value - 1).toString()})
        // setSelectedFilters({...cleanedFilters, page: value})
        setDisplayPage(value)
    }

    const onClickMARC21 = (item: BasicDescriptorInfo) => {
        setSelectedNlpId(item.nlpId)
        setSelectedSourceName(item.sourceName)
        setOpenSeeMARC21RecordDialog(true)
    }

    const cleanExtraFilters = () => {
        const updatedFilters = _.omit({...selectedFilters}, [...EXTRA_FILTERS, 'descriptorType']);
        setQueryParams(updatedFilters)
        setSelectedFilters(updatedFilters)
        setSelectedExtraFilters({})
    }

    const handleDeleteExtraFilter = (name: string, value: string) => {
        const extraFilters = selectedExtraFilters
        extraFilters[name] = extraFilters[name].filter(f => f !== value)
        setSelectedExtraFilters(extraFilters)
        setSelectedFilters({...selectedFilters, ...extraFilters})
        setQueryParams({...selectedFilters, ...extraFilters})

    }
    const handleDeleteFilter = (filterValue: string, type: string) => {
        const newFilters = filters.map(item => {
            if (item.fullName === filterValue) {
                item.isSelected = false
                item.value = null
            }
            return item
        })
        setFilters(newFilters)
        updateFiltersAndQueryParams(type, newFilters)

        if (type === 'descriptor_type' && !_.isEmpty(selectedExtraFilters)) {
            // removing all extra filters if descriptor type was deleted
            cleanExtraFilters()

        }

    }

    const handleDeleteSearchedText = () => {
        const cleanedFilters = omit(selectedFilters, ['searchedText']);
        setSelectedFilters({...cleanedFilters})
        setQueryParams({...cleanedFilters})
    }

    const renderSelectedFilters = () => {
        const selectedQueryFilter = queryParams.get('searchedText') ? <Chip key={queryParams.get('searchedText')}
                                                                            className={'filter-chip'}
                                                                            label={`${translateKey('searchedText', language)}: ${queryParams.get('searchedText')}`}
                                                                            onDelete={handleDeleteSearchedText}/> : null
        const descriptorTypeFilters = filters.filter(item => item.isSelected && item.type === 'descriptor_type')
            .map(item =>
                <Chip key={item.fullName}
                      className={'filter-chip'}
                      label={`${translateKey('descriptorType', language)}: ${translateKey(item.fullName, language)}`}
                      onDelete={() => handleDeleteFilter(item.fullName, item.type)}/>)

        const sourceFilters = filters.filter(item => item.isSelected && item.type === 'source' && item.label !== undefined)
            .map(item =>
                <Chip
                    key={item.fullName}
                    className={'filter-chip'}
                    label={`${translateKey('Files', language)}:  ${translateKey(item.fullName, language)}`}
                    onDelete={() => handleDeleteFilter(item.fullName, item.type)}/>)

        const dateFilters = filters.filter(item => item.isSelected && item.type === "date" && (item.value?.from || item.value?.to))
            .map(item =>
                <Chip key={item.fullName} className={'filter-chip'}
                      label={`${translateKey(item.fullName, language)}: ${item.value?.from?.toLocaleDateString() ?? '...'} - ${item.value?.to?.toLocaleDateString() ?? '...'}`}
                      onDelete={() => handleDeleteFilter(item.fullName, item.type)}/>)


        let extraFiltersChipsData = Object.entries(selectedExtraFilters).reduce((accumulator: {
            [key: string]: string
        }[], [key, values]) => {
            values.forEach(val => {
                accumulator.push({name: key, value: val});
            });
            return accumulator;
        }, []);

        const extraFilters = extraFiltersChipsData.map(item =>
            <Chip key={item.value}
                  className={'filter-chip'}
                  label={`${translateKey(item.name, language)}: ${item.value}`}
                  onDelete={() => handleDeleteExtraFilter(item.name, item.value)}/>
        )
        if (selectedQueryFilter === null && sourceFilters.length === 0 && descriptorTypeFilters.length === 0 && dateFilters.length === 0) {
            return <Box className={'selected-filters'}>
                <Typography
                    className={'selected-filters-label'}>{translateKey('selectedFilters', language)}: {translateKey('None', language)}</Typography>
            </Box>
        }

        return <Box className={'selected-filters'}>
            <Typography className={'selected-filters-label'}>
                {translateKey('selectedFilters', language)}:</Typography>{selectedQueryFilter}{sourceFilters}{descriptorTypeFilters}{dateFilters}{extraFilters}
        </Box>
    }

    const renderSortSelect = () => <FormControl className={'sort-box'}>

        <FormLabel id={'sort-select-label'}
                   aria-label={translateKey('sortByReader', language)}>{translateKey('sortBy:', language)}
            <Select labelId={'sort-select-label'} size={'small'} value={selectedSort}
                    onChange={handleSortModeChange}
                    variant={'outlined'}>
                {Object.entries(SORT_MODES).map(([key, value]) => <MenuItem
                    value={value}
                    key={value}>{translateKey(value, language)}</MenuItem>)}
            </Select></FormLabel>
    </FormControl>


    const renderSortRadioGroup = () => <FormControl style={{padding: 16}}>
        <Box className={'row-space-between'}>
            <FormLabel id="sort-radio-buttons-group-label"
                       className={'upper-case bold-text'}>{translateKey('sort', language)}</FormLabel>
            <CloseIcon onClick={() => setShowSortDrawer(false)} aria-label={'close'}/>
        </Box>
        <RadioGroup
            aria-labelledby="sort-radio-buttons-group-label"
            value={selectedSort}
            name="radio-buttons-group"
            onChange={handleSortModeChange}
        >
            {Object.entries(SORT_MODES).map(([key, value]) => <FormControlLabel key={key} value={value}
                                                                                control={<Radio/>}
                                                                                label={translateKey(value, language)}/>)}
        </RadioGroup>
    </FormControl>

    const renderDescriptorsList = (dataToRender: BasicDescriptorInfo[], isAlternate: boolean = false) => <List>
        {dataToRender.map((item: BasicDescriptorInfo) => {
                return displayMode === 'cards' ?
                    <ListItem key={item.nlpId}>
                        <Suspense fallback={<Box sx={{width: '100%'}}>
                            <LinearProgress/>
                        </Box>}><DescriptorCard item={item} navigateToDescriptorDetails={navigateToDescriptorDetails}
                                                isAlternate={isAlternate}/>
                        </Suspense>
                    </ListItem> : <><ListItem disablePadding key={item.nlpId}
                                              className={isAlternate ? 'list-item-alternate' : 'list-item'}>
                        <ListItemButton onClick={() => navigateToDescriptorDetails(item.nlpId)}
                                        className={'list-item-name'}>
                            <Typography dangerouslySetInnerHTML={{
                                __html: isAlternate ? `<b>${highlightName(item.preferredName, searchedText)}</b> [${
                                    translateKey(item.type,
                                        language)}] <br><small>${translateKey('tracing', language).toLowerCase()}</small>` : `<b>${highlightName(item.preferredName, searchedText)}</b> <br>[${
                                    translateKey(item.type,
                                        language)}]`
                            }}/>


                        </ListItemButton>
                        <Button variant={'text'} size={'small'} className={'marc21-btn-list-view'}
                                onClick={() => onClickMARC21(item)}
                                startIcon={<InsertDriveFileIcon/>}>MARC 21</Button>

                    </ListItem>

                    </>
            }
        )
        }

    </List>

    const renderSimilarRecords = () => {
        if (!!similarRecords && (!!similarRecords.DBN || !!similarRecords.MESH)) {
            return <DescriptorSimilarObjects records={similarRecords}/>
        } else {
            return <Typography
                style={{marginLeft: 15, marginTop: 10}}>{translateKey('noResultsFound', language)}</Typography>
        }
    }

    const renderFiltersDrawer = () => <Drawer anchor={"left"} open={showFilterDrawer} variant={'temporary'}
                                              onClose={() => setShowFilterDrawer(false)}
    >

        <Box overflow={'scroll'} height={'100%'}>

            <Suspense fallback={<Box sx={{width: '100%'}}>
                <LinearProgress/>
            </Box>}><FiltersBox filters={filters} extraFilters={selectedExtraFilters} onSelectFilter={onSelectFilter}
                                onSelectExtraFilter={onSelectExtraFilter}
                                onSelectMultipleExtraFilters={onSelectMultipleExtraFilters}
                                mobileView
                                onCloseFilters={() => setShowFilterDrawer(false)}
                                onSelectDateFilter={onSelectDateFilter} aggregations={aggregations}/></Suspense>
        </Box>
    </Drawer>

    const renderSortDrawer = () => <Drawer anchor={"top"} open={showSortDrawer}
                                           onClose={() => setShowSortDrawer(false)}
    >
        {renderSortRadioGroup()}
    </Drawer>


    const renderPaginationBox = () => <Suspense fallback={<Box sx={{width: '100%'}}>
        <LinearProgress/>
    </Box>}>
        <Pagination count={totalPages > process.env.REACT_APP_MAX_PAGE_IN_PAGINATION ? process.env.REACT_APP_MAX_PAGE_IN_PAGINATION : totalPages} page={displayPage}
                    onChange={onChangePage} color={"primary"} size={'large'}
                    className={'search-list-pagination'}/>

    </Suspense>

    const renderDisplayModeToggle = () => <ToggleButtonGroup
        value={displayMode}
        exclusive
        onChange={(event, value) => {
            setDisplayMode(value)
            setCookie('displayMode', value)
        }}
        aria-label="display mode"
        size={'small'}
        className={'display-mode-group'}
    >
        <ToggleButton value="cards" aria-label="cards">
            <GridViewIcon color={'primary'}/>
        </ToggleButton>
        <ToggleButton value="list" aria-label="list">
            <FormatListBulletedIcon color={'primary'}/>
        </ToggleButton>
    </ToggleButtonGroup>

    const renderDesktopView = () => <>
        {loading ? <Box sx={{width: '100%'}}>
            <LinearProgress/>
        </Box> : <>
            <h1 className="screen-reader-text">{translateKey('browseDatabaseScreenReaderHeader', language)}</h1>

            <EmptyDivider size={'medium'}/>
            <Suspense fallback={<Box sx={{width: '100%'}}>
                <LinearProgress/>
            </Box>}>
                <Grid container style={{justifyContent: 'center'}}>
                    <Grid size={{sm: 3}}>
                        <Box style={{marginTop: '-20px'}}>

                            <BreadcrumbsRow content={translateKey('Browsing', language)}/>
                        </Box>
                        <EmptyDivider size={'medium'}/>

                        <FiltersBox filters={filters} extraFilters={selectedExtraFilters}
                                    onSelectExtraFilter={onSelectExtraFilter}
                                    onSelectFilter={onSelectFilter}
                                    onSelectMultipleExtraFilters={onSelectMultipleExtraFilters}
                                    onSelectDateFilter={onSelectDateFilter}
                                    aggregations={aggregations}/>
                    </Grid>
                    <Grid size={{sm: 6}}>
                        <EmptyDivider size={'medium'}/>

                        <Box className={'row-space-between header-box'}>

                            <Typography textAlign={'left'} variant={'h2'}
                                        className={'h1-header'}>{translateKey('Files', language)}</Typography>

                            {renderDisplayModeToggle()}
                        </Box>
                        {renderSelectedFilters()}
                        {!!result.length && <Box className={'sort-results-box'}>
                            {renderSortSelect()}
                            {alternateNamesTotalRecords > 0 ? <Typography
                                className={'results-info'}>{translateKey('results', language)}: <b>{totalRecords}</b> (+{alternateNamesTotalRecords} {translateKey('tracing', language)})
                            </Typography> : <Typography
                                className={'results-info'}>{translateKey('results', language)}: <b>{totalRecords}</b>
                            </Typography>}
                        </Box>}

                        {!!alternateNamesResult.length && renderDescriptorsList(alternateNamesResult, true)}
                        {!!result.length ? renderDescriptorsList(result) : renderSimilarRecords()}
                        {!!result.length && renderPaginationBox()}
                    </Grid>
                </Grid>
            </Suspense>
        </>}
    </>


    const renderMobileView = () => <Box>
        <BreadcrumbsRow content={translateKey('Browsing', language)}/>

        <Box className={'mobile-descriptors-list'}>
            <h1 className="screen-reader-text">{translateKey('browseDatabaseScreenReaderHeader', language)}</h1>

            {!!result.length && <Button className={'filter-mobile-btn'} variant={"outlined"} size={'large'}
                                        onClick={() => setShowFilterDrawer(true)}>{translateKey('filterVerb', language)}</Button>}
            {!!result.length && <Button className={'filter-mobile-btn'} variant={"contained"} size={'large'}
                                        onClick={() => setShowSortDrawer(true)}>{translateKey('sortVerb', language)}</Button>}
        </Box>
        <Box className={'padded-mobile-box'}>
            {renderSelectedFilters()}</Box>
        <Box className={'padded-mobile-box'}>

            <Typography>{translateKey('results', language)}: {totalRecords}</Typography>
        </Box>

        {!!alternateNamesResult.length && renderDescriptorsList(alternateNamesResult, true)}

        {!!result.length ? renderDescriptorsList(result) : renderSimilarRecords()}

        {!!result.length && renderPaginationBox()}
        {!!result.length && renderFiltersDrawer()}
        {!!result.length && renderSortDrawer()}
    </Box>


    return <Box>
        <SEO
            description={'Przeglądaj bazę danych Deskryptorów Biblioteki Narodowej i MeSH-POL (Medical Subject Headings-POL).'}/>
        <EmptyDivider size={'small'}/>
        <Suspense fallback={<Box sx={{width: '100%'}}>
            <LinearProgress/>
        </Box>}>
            <Box style={{marginLeft: '13%'}}>
            </Box>
            {mobileView ? renderMobileView() : renderDesktopView()}
            {selectedNlpId && selectedSourceName &&
                <MARC21RecordDialog nlpId={selectedNlpId} open={openSeeMARC21RecordDialog}
                                    sourceName={selectedSourceName}
                                    onClose={onCloseSeeMARC21RecordDialog}/>}
        </Suspense>
    </Box>

}

export default DescriptorsList